



























































































import { Vue, Component, Prop } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate-3';

import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import CheckboxProvider, {
  CheckboxProviderVariant
} from '@/componentsV2/molecules/Inputs/CheckboxProvider.vue';

import { EConnectionTypeProvider, IProvider } from '@/models/provider';

import MerchantService from '@/api/MerchantService';

import { setFocusOnFirstInput } from '@/utils/setFocusOnFirstInput';
import { treatUrl } from '@/utils/treatUrl';

interface IProviderItem extends IProvider {
  requireModalUrl?: boolean;
}

export type FormData = {
  provider: string;
  link: string;
  links?: Record<string, any>;
};

let timer: NodeJS.Timeout;

@Component({
  components: {
    ValidationObserver,
    ValidationProvider,
    CheckboxProvider,
    FilledButton
  }
})
export default class ProviderConnectionForm extends Vue {
  @Prop({ default: () => ({ provider: '', link: '' }) })
  defaultFormData!: FormData;
  @Prop({ default: '' }) businessOperationType?: string;
  @Prop({ default: false }) loading?: boolean;
  @Prop({ default: false }) showWithoutItem?: boolean;
  @Prop({ default: () => [] }) excludeTypes!: string[];
  @Prop({ default: 'Connect' }) submitLabel?: string;
  @Prop({ default: 'sm' }) variant?: CheckboxProviderVariant;
  @Prop({ default: true }) showLetUsKnow?: boolean;
  @Prop({ default: () => Boolean }) filterOptions!: (item: any) => boolean;

  merchantService = new MerchantService();

  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };
  activeProvider = this.defaultFormData.provider;
  formData: Record<string, string> = this.formDataFactory;
  otherPlatform: string = '';

  get providerList(): Array<IProviderItem> {
    return (
      this.$store.getters['merchant/getProvidersList'] ||
      ([] as Array<IProviderItem>)
    );
  }

  get businessState() {
    return this.$store.getters['business/getBusinessState'] || {};
  }

  get formDataFactory() {
    return this.availableProviders.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.name]:
          this.defaultFormData.provider === curr.name
            ? this.defaultFormData.link
            : ''
      }),
      {}
    );
  }

  get withoutProvider() {
    return {
      name: 'without',
      displayName: 'Other'
    } as IProviderItem;
  }

  get availableProviders() {
    const filteredProviders = this.providerList
      .filter(
        provider =>
          provider.status === 'active' &&
          provider.displayAsSignupOption &&
          provider.authType === EConnectionTypeProvider.OAUTH
      )
      .filter(provider =>
        provider.availableFor.includes(this.businessOperationType || '')
      )
      .filter(provider => !this.excludeTypes.includes(provider.type))
      .filter(this.filterOptions);

    if (this.showWithoutItem) {
      filteredProviders.push(this.withoutProvider);
    }

    return filteredProviders;
  }

  get getSubmitLabel() {
    if (this.activeProvider === 'other') {
      return 'Notify me';
    }

    return this.submitLabel;
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  async mounted() {
    await this.getProviderList();
  }

  async getProviderList() {
    if (this.providerList.length) return;

    const { data, status } = await this.merchantService.listProviders();

    if (status !== 200) return;

    this.$store.dispatch('merchant/SET_PROVIDERS_LIST', data.providers);
  }

  beforeDestroy() {
    if (timer) clearTimeout(timer);
  }

  setInputFocus(providerName: string) {
    timer = setTimeout(() => {
      const containerEl = document.getElementById(providerName)!;
      const inputEl = containerEl.querySelector('input');

      if (inputEl) inputEl.focus();
    }, 0);
  }

  async handleToggle(providerName: string) {
    const formRef = this.$refs.observer;

    formRef.reset();

    if (this.activeProvider === providerName) {
      this.activeProvider = '';

      return;
    }

    this.activeProvider = providerName;
    this.setInputFocus(providerName);
  }

  getFormData() {
    if (this.activeProvider === 'other') {
      return {
        provider: 'other',
        platformName: this.otherPlatform
      };
    }

    const [provider, link] = Object.entries(this.formData).find(
      ([key]) => this.activeProvider === key
    )!;

    const selectedProvider = this.availableProviders.find(
      providerData => providerData.name === provider
    );

    return {
      provider,
      link: treatUrl(String(link)),
      links: selectedProvider ? selectedProvider.links : {}
    };
  }

  onBack() {
    this.$emit('back');
  }

  async onSubmit() {
    const formRef = this.$refs.observer;

    const formIsValid = await formRef.validate();

    if (!formIsValid) {
      if (this.activeProvider) {
        setFocusOnFirstInput('input[aria-invalid="true"]');
      } else {
        this.$notification('Please select an option', 'error');
      }

      return;
    }

    this.$emit('submit', this.getFormData());
  }
}
